/**
 * @file VenueContext.tsx
 * @description Global Context Provider managing the currently authenticated venue, 
 * user profile, active organization, and permissions.
 * 
 * KEY RESPONSIBILITIES:
 * 1. Tracks all venues belonging to the logged-in user (`venues`).
 * 2. Manages the currently selected active branch (`activeVenue`).
 * 3. Resolves user roles across venue and organization levels (`activeMemberRole`, `orgRole`).
 * 4. Listens to Supabase Auth state changes with automatic session synchronization.
 * 5. Handles graceful token refresh error recovery to avoid endless reload loops.
 */

import React, { useEffect, useState, useRef } from 'react';
import { supabase } from '../lib/supabase';
import type { Venue, Profile, TeamMemberPermissions } from '../types';
import { usePostHog } from '@posthog/react';
import * as Sentry from '@sentry/react';
import { VenueContext } from './VenueContextInstance';
import { fetchPulseLiveData } from '../features/analytics/services/analytics.service';
import { primePulseDataCache } from '../features/analytics/hooks/usePulseData';

export const VenueProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // Collection of all venues the current user has access to
  const [venues, setVenues] = useState<Venue[]>([]);
  // The specific venue currently selected in the admin dashboard
  const [activeVenue, setActiveVenueState] = useState<Venue | null>(null);
  // User profile metadata from the `profiles` table
  const [profile, setProfile] = useState<Profile | null>(null);
  // Current user's role in the active venue (e.g. 'OWNER', 'MANAGER', 'CHEF', 'WAITER')
  const [activeMemberRole, setActiveMemberRole] = useState<string | null>(null);
  // Current user's granular permissions in the active venue
  const [activeMemberPermissions, setActiveMemberPermissions] = useState<TeamMemberPermissions>({});
  // Active organization UUID if the venue belongs to a multi-branch group
  const [organizationId, setOrganizationId] = useState<string | null>(null);
  // Role at the organization level
  const [orgRole, setOrgRole] = useState<string | null>(null);
  // Authenticated user's email address
  const [authEmail, setAuthEmail] = useState<string | null>(null);
  // Whether user's email has been verified
  const [isEmailConfirmed, setIsEmailConfirmed] = useState<boolean | null>(null);
  // Loading indicator for initial auth & venue synchronization
  const [isLoading, setIsLoading] = useState(true);
  const posthog = usePostHog();
  
  // Cache user ID in a ref to avoid redundant network calls to supabase.auth.getUser()
  const cachedUserIdRef = useRef<string | null>(null);
  // Cache user metadata in a ref to track cross-device user preferences (e.g. last_venue_id)
  const cachedUserMetaRef = useRef<Record<string, any>>({});
  // Lookup map: [organizationId] -> role
  const organizationRolesRef = useRef<Record<string, string>>({});

  /**
   * Fetches venues, profile, and memberships in a single parallel query batch.
   * @param forceLoading If true, forces the loading spinner to display.
   */
  const fetchVenues = async (forceLoading = false) => {
    // Only show loading if we don't have data yet OR if explicitly requested
    if (forceLoading || (!venues.length && !profile)) {
      setIsLoading(true);
    }

    try {
      // Step 0: Inspect URL for support impersonation session token
      if (typeof window !== 'undefined') {
        const urlParams = new URLSearchParams(window.location.search);
        const tokenParam = urlParams.get('support_token');
        if (tokenParam) {
          try {
            const decoded = JSON.parse(atob(tokenParam));
            if (decoded?.venue_id && (!decoded.expires_at || new Date(decoded.expires_at).getTime() > Date.now())) {
              sessionStorage.setItem('tablegreet_support_session', JSON.stringify(decoded));
              localStorage.setItem('lastVenueId', decoded.venue_id);
            }
          } catch (e) {
            console.warn('Failed to decode support session token:', e);
          }
        }
      }

      // Step 1: Verify current authenticated user session
      let userId = cachedUserIdRef.current;
      if (!userId) {
        const { data: { user }, error: authError } = await supabase.auth.getUser();

        if (authError) {
          // Detect stale or revoked refresh tokens and safely sign out
          const isRefreshError = authError.status === 400 ||
                                authError.message?.includes('Refresh Token') ||
                                (authError as { code?: string }).code === 'refresh_token_not_found';

          if (isRefreshError) {
            try {
              await supabase.auth.signOut();
            } catch (signOutErr) {
              console.warn('Signout failed during token refresh error:', signOutErr);
            }
            setIsLoading(false);
            return;
          }
          console.warn('VenueContext Auth Error:', authError.message);
        }

        if (!user) {
          setAuthEmail(null);
          setIsEmailConfirmed(null);
          setIsLoading(false);
          return;
        }
        userId = user.id;
        cachedUserIdRef.current = userId;
        cachedUserMetaRef.current = user.user_metadata || {};
        setAuthEmail(user.email || null);
        setIsEmailConfirmed(Boolean(user.email_confirmed_at));
      }

      // Check for active internal support impersonation session
      let supportSession: { venue_id: string; expires_at?: string; venue_name?: string; venue_slug?: string } | null = null;
      try {
        const raw = sessionStorage.getItem('tablegreet_support_session');
        if (raw) {
          const parsed = JSON.parse(raw);
          if (!parsed.expires_at || new Date(parsed.expires_at).getTime() > Date.now()) {
            supportSession = parsed;
          } else {
            sessionStorage.removeItem('tablegreet_support_session');
          }
        }
      } catch {
        // Suppresses malformed session storage parsing errors
      }

      const backendLastVenueId = cachedUserMetaRef.current?.last_venue_id;
      const localLastVenueId = typeof window !== 'undefined' ? localStorage.getItem('lastVenueId') : null;
      const targetVenueId = supportSession?.venue_id || backendLastVenueId || localLastVenueId;

      interface VenueMembership {
        venue_id: string;
        role: string;
        permissions?: TeamMemberPermissions | null;
        custom_role_id?: string | null;
        venues: Venue;
      }

      interface OrganizationMembership {
        organization_id: string | null;
        role: string | null;
      }

      // Executes the consolidated bootstrap RPC to retrieve all operational state in a single database roundtrip
      let bootstrapLoaded = false;
      try {
        const { data: bootstrap, error: bootstrapErr } = await supabase.rpc(
          'get_venue_admin_bootstrap_state',
          { p_target_venue_id: targetVenueId || null }
        );

        if (!bootstrapErr && bootstrap) {
          bootstrapLoaded = true;

          // Hydrates user profile information
          if (bootstrap.profile) {
            setProfile(bootstrap.profile as Profile);
          }

          // Hydrates organization role mappings
          const orgMemberships = (bootstrap.organization_memberships || []) as OrganizationMembership[];
          const organizationRoleMap: Record<string, string> = {};
          orgMemberships.forEach((m) => {
            if (m.organization_id && m.role) {
              organizationRoleMap[m.organization_id] = m.role;
            }
          });
          organizationRolesRef.current = organizationRoleMap;

          // Extracts venue objects and builds granular role and permission maps
          const memberships = (bootstrap.venue_memberships || []) as unknown as VenueMembership[];
          let typedVenues: Venue[] = memberships
            .map((m) => (Array.isArray(m.venues) ? m.venues[0] : m.venues))
            .filter(Boolean) as Venue[];

          const memberRoleMap: Record<string, string> = {};
          const memberPermissionsMap: Record<string, TeamMemberPermissions> = {};
          memberships.forEach((m) => {
            if (m.venue_id) {
              memberRoleMap[m.venue_id] = m.role;
              memberPermissionsMap[m.venue_id] = (m.permissions as TeamMemberPermissions) || {};
            }
          });

          // Injects support impersonation venue if operating under an active support session
          if (supportSession?.venue_id) {
            const alreadyInList = typedVenues.find(v => v.id === supportSession?.venue_id);
            if (!alreadyInList) {
              const { data: directVenue } = await supabase
                .from('venues')
                .select('*')
                .eq('id', supportSession.venue_id)
                .maybeSingle();

              if (directVenue) {
                typedVenues = [directVenue as Venue, ...typedVenues];
                memberRoleMap[supportSession.venue_id] = 'OWNER';
                memberPermissionsMap[supportSession.venue_id] = {};
              }
            } else {
              memberRoleMap[supportSession.venue_id] = 'OWNER';
            }
          }

          if (typedVenues.length > 0) {
            setVenues(typedVenues);

            // Selects the target venue or falls back to the first available venue
            const saved = typedVenues.find(v => v.id === targetVenueId);
            const nextActive = saved || typedVenues[0];
            setActiveVenueState(nextActive);

            // Registers venue attributes as persistent super properties and multi-tenant group in PostHog
            posthog?.register({
              venue_id: nextActive.id,
              venue_name: nextActive.name,
              venue_slug: nextActive.slug,
            });
            posthog?.group('venue', nextActive.id, {
              name: nextActive.name,
              slug: nextActive.slug,
            });

            setActiveMemberRole(memberRoleMap[nextActive.id] || null);
            setActiveMemberPermissions(memberPermissionsMap[nextActive.id] || {});
            const nextOrganizationId = nextActive.organization_id || null;
            setOrganizationId(nextOrganizationId);
            setOrgRole(nextOrganizationId ? organizationRoleMap[nextOrganizationId] || null : null);

            // Pre-primes live telemetry cache to ensure dashboard and pulse cards render simultaneously
            if (bootstrap.pulse) {
              primePulseDataCache(nextActive.id, bootstrap.pulse);
            }

            // Seeds platform broadcast announcement into session cache to avoid separate Edge Function invocation
            if (bootstrap.broadcast) {
              sessionStorage.setItem('tablegreet_platform_broadcast', JSON.stringify(bootstrap.broadcast));
            } else {
              sessionStorage.removeItem('tablegreet_platform_broadcast');
            }

            // Seeds recent venue notifications into session cache for instant dropdown display
            if (Array.isArray(bootstrap.notifications) && bootstrap.notifications.length > 0) {
              sessionStorage.setItem(`venue_notifications_${nextActive.id}`, JSON.stringify(bootstrap.notifications));
            }
          } else {
            setVenues([]);
            setActiveVenueState(null);
            setActiveMemberRole(null);
            setActiveMemberPermissions({});
            setOrganizationId(null);
            setOrgRole(null);
          }
        }
      } catch (bootstrapRpcError) {
        console.warn('Bootstrap RPC unavailable, using parallel query fallback:', bootstrapRpcError);
      }

      // Step 2 Fallback: Parallel individual entity queries if the consolidated RPC is unavailable
      if (!bootstrapLoaded) {
        const prewarmPromise = localLastVenueId 
          ? fetchPulseLiveData(localLastVenueId).catch(() => null) 
          : Promise.resolve(null);

        const [profileResult, membershipsResult, orgResult, prewarmedPulse] = await Promise.all([
          supabase
            .from('profiles')
            .select('*')
            .eq('id', userId)
            .maybeSingle(),
          supabase
            .from('venue_members')
            .select('venue_id, role, permissions, custom_role_id, venues(*)')
            .eq('user_id', userId),
          supabase
            .from('organization_members')
            .select('role, organization_id')
            .eq('user_id', userId),
          prewarmPromise,
        ]);

        if (profileResult.error) console.error('Profile fetch error:', profileResult.error);
        if (membershipsResult.error) console.error('Memberships fetch error:', membershipsResult.error);
        if (orgResult?.error) console.error('Organization fetch error:', orgResult.error);

        if (profileResult.data) {
          setProfile(profileResult.data as Profile);
        }

        const memberships = membershipsResult.data;
        const organizationMemberships = (orgResult.data || []) as OrganizationMembership[];
        let typedVenues: Venue[] = [];
        const memberRoleMap: Record<string, string> = {};
        const memberPermissionsMap: Record<string, TeamMemberPermissions> = {};
        const organizationRoleMap: Record<string, string> = {};

        organizationMemberships.forEach((membership) => {
          if (membership.organization_id && membership.role) {
            organizationRoleMap[membership.organization_id] = membership.role;
          }
        });
        organizationRolesRef.current = organizationRoleMap;

        if (memberships && memberships.length > 0) {
          typedVenues = (memberships as unknown as VenueMembership[])
            .map((m) => {
              if (!m.venues) return null;
              return Array.isArray(m.venues) ? m.venues[0] : m.venues;
            })
            .filter(Boolean) as Venue[];

          (memberships as unknown as VenueMembership[]).forEach((m) => {
            if (m.venue_id) {
              memberRoleMap[m.venue_id] = m.role;
              memberPermissionsMap[m.venue_id] = (m.permissions as TeamMemberPermissions) || {};
            }
          });
        }

        if (supportSession?.venue_id) {
          const alreadyInList = typedVenues.find(v => v.id === supportSession?.venue_id);
          if (!alreadyInList) {
            const { data: directVenue } = await supabase
              .from('venues')
              .select('*')
              .eq('id', supportSession.venue_id)
              .maybeSingle();

            if (directVenue) {
              typedVenues = [directVenue as Venue, ...typedVenues];
              memberRoleMap[supportSession.venue_id] = 'OWNER';
              memberPermissionsMap[supportSession.venue_id] = {};
            }
          } else {
            memberRoleMap[supportSession.venue_id] = 'OWNER';
          }
        }

        if (typedVenues.length > 0) {
          setVenues(typedVenues);

          const saved = typedVenues.find(v => v.id === targetVenueId);
          const nextActive = saved || typedVenues[0];
          setActiveVenueState(nextActive);

          setActiveMemberRole(memberRoleMap[nextActive.id] || null);
          setActiveMemberPermissions(memberPermissionsMap[nextActive.id] || {});
          const nextOrganizationId = nextActive.organization_id || null;
          setOrganizationId(nextOrganizationId);
          setOrgRole(nextOrganizationId ? organizationRoleMap[nextOrganizationId] || null : null);

          if (prewarmedPulse && nextActive.id === localLastVenueId) {
            primePulseDataCache(nextActive.id, prewarmedPulse);
          } else if (nextActive?.id) {
            try {
              const freshPulse = await fetchPulseLiveData(nextActive.id);
              if (freshPulse) {
                primePulseDataCache(nextActive.id, freshPulse);
              }
            } catch {
              // Non-blocking fallback
            }
          }
          setVenues([]);
          setActiveVenueState(null);
          setActiveMemberRole(null);
          setActiveMemberPermissions({});
          setOrganizationId(null);
          setOrgRole(null);
        }
      }
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to fetch venue context';
      console.error('[VenueContext Error]', message, err);
      // Report venue-context fetch failures to Sentry — this error path is
      // caught by try/catch so it would otherwise never reach the error boundary.
      Sentry.captureException(err, { extra: { context: 'fetchVenues' } });
    } finally {
      setIsLoading(false);
    }
  };

  const setActiveVenue = (venue: Venue) => {
    setActiveVenueState(venue);

    // Updates persistent PostHog super properties and captures venue switch telemetry
    posthog?.register({
      venue_id: venue.id,
      venue_name: venue.name,
      venue_slug: venue.slug,
    });
    posthog?.group('venue', venue.id, {
      name: venue.name,
      slug: venue.slug,
    });
    posthog?.capture('venue_switched', {
      venue_id: venue.id,
      venue_name: venue.name,
      venue_slug: venue.slug,
    });

    // Optimistically clear the role to prevent showing the previous venue's role
    // while the async network lookup resolves.
    setActiveMemberRole(null);
    localStorage.setItem('lastVenueId', venue.id);
    cachedUserMetaRef.current = {
      ...cachedUserMetaRef.current,
      last_venue_id: venue.id
    };
    // Sync preferred active venue across devices via Supabase Auth user metadata
    supabase.auth.updateUser({
      data: { last_venue_id: venue.id }
    }).catch(err => {
      console.warn('Could not sync last_venue_id to user metadata:', err);
    });
    const nextOrganizationId = venue.organization_id || null;
    setOrganizationId(nextOrganizationId);
    setOrgRole(nextOrganizationId ? organizationRolesRef.current[nextOrganizationId] || null : null);

    // Use the cached user ID — no network call needed for role lookup
    const userId = cachedUserIdRef.current;
    if (userId) {
      supabase
        .from('venue_members')
        .select('role, permissions')
        .eq('user_id', userId)
        .eq('venue_id', venue.id)
        .maybeSingle()
        .then(({ data }) => {
          setActiveMemberRole(data?.role || null);
          setActiveMemberPermissions((data?.permissions as TeamMemberPermissions) || {});
        });
    }
  };

  useEffect(() => {
    let active = true;

    // 1. Get initial session
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (!active) return;
      if (session?.user) {
        setAuthEmail(session.user.email || null);
        setIsEmailConfirmed(Boolean(session.user.email_confirmed_at));
        cachedUserIdRef.current = session.user.id;

        // Identify the returning user immediately so any errors or events
        // fired during this page load are attributed correctly in both
        // Sentry and PostHog — not treated as anonymous sessions.
        Sentry.setUser({ id: session.user.id, email: session.user.email });
        posthog?.identify(session.user.id, { email: session.user.email });

        fetchVenues(true);
      } else {
        setIsLoading(false);
      }
    });

    // 2. Listen to subsequent auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (!active) return;

      if (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED' || event === 'USER_UPDATED') {
        if (session?.user) {
          setAuthEmail(session.user.email || null);
          setIsEmailConfirmed(Boolean(session.user.email_confirmed_at));
          Sentry.setUser({ id: session.user.id, email: session.user.email });

          // Only re-fetch if the user actually changed to prevent flickering
          if (session.user.id !== cachedUserIdRef.current) {
            cachedUserIdRef.current = session.user.id;
            fetchVenues(true);
          }
        }
      } else if (event === 'SIGNED_OUT') {
        posthog?.reset();
        Sentry.setUser(null);
        cachedUserIdRef.current = null;
        setVenues([]);
        setActiveVenueState(null);
        setActiveMemberRole(null);
        setActiveMemberPermissions({});
        setProfile(null);
        setOrganizationId(null);
        setOrgRole(null);
        organizationRolesRef.current = {};
        setAuthEmail(null);
        setIsEmailConfirmed(null);
        setIsLoading(false);
      }
    });

    return () => {
      active = false;
      subscription.unsubscribe();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <VenueContext.Provider value={{
      venues,
      activeVenue,
      profile,
      activeMemberRole,
      activeMemberPermissions,
      organizationId,
      orgRole,
      authEmail,
      isEmailConfirmed,
      isLoading,
      setActiveVenue,
      refreshVenues: fetchVenues
    }}>
      {children}
    </VenueContext.Provider>
  );
};

// Moved to src/hooks/useVenues.ts
export { useVenues } from '../hooks/useVenues';
